import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "standalone", "trailing", "use", "setStyledClassName", "$cssStyles", "renderGripOnHover"],
  _excluded2 = ["children", "position", "size", "use"];
import { cloneElement, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { warnIfFragment } from '../../utils/warnIfFragment/warnIfFragment';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { ChildrenWrapper } from '../ChildrenWrapper/ChildrenWrapper';
import { findDOMNode } from '../../../utils/findDOMNode';
import { useDragHandleHoverContainerContext } from '../DragHandleHoverContainer/DragHandleHoverContainerProvider';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DRAG_HANDLE_WIDTH = '5px';
const DRAG_HANDLE_HEIGHT = '24px';
const emptyBox = /*#__PURE__*/_jsx("span", {});
const maxHeightForSize = {
  sm: 10,
  md: 50,
  lg: 90
};
const getGrippieBackground = ({
  use,
  theme
}) => {
  const fill = use === 'on-dark' ? theme.color['icon-core-on-fill-default'] : theme.color['icon-tertiary-default'];
  const svgStr = `<svg width='${DRAG_HANDLE_WIDTH}' height='${DRAG_HANDLE_HEIGHT}' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${DRAG_HANDLE_WIDTH} ${DRAG_HANDLE_HEIGHT}'><path fill='${fill}' d='M0 0h2v2H0V0zm0 8h2v2H0V8zm0 8h2v2H0v-2zM0 4h2v2H0V4zm0 8h2v2H0v-2zm0 8h2v2H0v-2zM3 0h2v2H3V0zm0 8h2v2H3V8zm0 8h2v2H3v-2zM3 4h2v2H3V4zm0 8h2v2H3v-2zm0 8h2v2H3v-2z'/></svg>`;
  return {
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svgStr)}")`
  };
};
const getGrippieOffset = ({
  standalone,
  trailing
}) => {
  let offsetX = '9px'; // TODO: swap for token when it becomes available
  if (standalone && trailing) {
    offsetX = '0';
  } else if (standalone) {
    offsetX = '50%';
  }
  if (trailing) {
    offsetX = `calc(100% - ${offsetX})`;
  }
  return {
    backgroundPosition: `${offsetX} calc(50% + 1px)`,
    backgroundRepeat: `no-repeat`
  };
};
const DragHandleChild = styled(_ref => {
  let {
      children,
      className,
      setStyledClassName
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  useEffect(() => {
    setStyledClassName(className);
  }, [className, setStyledClassName]);
  return /*#__PURE__*/cloneElement(children, Object.assign({}, rest));
})(({
  size,
  standalone,
  trailing,
  use,
  renderGripOnHover,
  theme,
  $cssStyles
}) => {
  const grippieBackground = getGrippieBackground({
    use,
    theme
  });
  const grippieOffset = getGrippieOffset({
    standalone,
    trailing
  });
  return Object.assign({}, !renderGripOnHover ? grippieBackground : {
    ['&:hover']: Object.assign({}, grippieBackground)
  }, grippieOffset, {
    cursor: 'grab'
  }, standalone && {
    display: 'inline-block',
    lineHeight: theme.typography['body-100'].lineHeight,
    minWidth: DRAG_HANDLE_WIDTH,
    /* By using hidden content instead of fixed height, we inherit line-height, vertical-align, etc. */
    ['&::after']: {
      content: '"."',
      visibility: 'hidden'
    }
  }, size !== 'default' && {
    backgroundImage: 'none !important',
    position: 'relative',
    '&::before': Object.assign({
      content: `""`
    }, grippieBackground, {
      backgroundRepeat: 'repeat-y',
      position: 'absolute',
      [trailing ? 'right' : 'left']: '9px',
      // TODO: swap for token when it becomes available
      top: 1.5,
      // handle was slightly too high, so we're offsetting it down
      width: DRAG_HANDLE_WIDTH,
      height: `calc(100% - ${DRAG_HANDLE_HEIGHT})`,
      maxHeight: `${maxHeightForSize[size]}px`,
      margin: `${theme.space['300']} ${theme.space['0']}`
    })
  }, $cssStyles && $cssStyles);
});
export const DragHandle = _ref2 => {
  let {
      children = emptyBox,
      position = 'start',
      size = 'default',
      use = 'default'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const [styledClassName, setStyledClassName] = useState('');
  const [targetClassName, setTargetClassName] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const lastAddedClassRef = useRef(null);
  warnIfFragment(children, 'DragHandle');
  useLayoutEffect(() => {
    if (!targetElement) return undefined;
    const observer = new MutationObserver(() => {
      setTargetClassName(targetElement.className);
    });

    // Observer configuration: observe changes in className attribute as changes
    // in focus state can change styled-components className. If there are changes
    // we need a way to track that and re-trigger the layout effect to re-apply
    // the original className that contains the drag handle styling
    observer.observe(targetElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => {
      observer.disconnect();
    };
  }, [targetElement]);

  // this is definitely weird & less than ideal, but the tl;dr here is:
  // 1. we can't pass the generated styled-components classname to the styled(Component) if Component is a
  // transitional component that doesn't accept a classname prop
  // 2. we also don't want to add forwardRef to _every_ component that could be a child of DragHandle (which is anything)
  // 3. so use findDOMNode to get a ref to the underlying wrapped/child component
  // 4. we then use a callback above to pass the generated styled-components classname back into this component
  // 5. from there, we can manually add the generated classname to the childrenWrapperRef
  useLayoutEffect(() => {
    if (targetElement && styledClassName) {
      const newClass = styledClassName.split(' ')[1];

      // Remove the previously added class if it exists
      if (lastAddedClassRef.current) {
        targetElement.classList.remove(lastAddedClassRef.current);
      }

      // Add the new class
      if (newClass) {
        targetElement.classList.add(newClass);
        lastAddedClassRef.current = newClass;
      }
    }
  }, [targetElement, styledClassName, targetClassName]);
  const {
    renderGripOnHover
  } = useDragHandleHoverContainerContext();
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(ChildrenWrapper
    // @ts-ignore - incompatible ref type
    , {
      ref: childNode => {
        setTargetElement(
        // eslint-disable-next-line react/no-find-dom-node
        findDOMNode(childNode));
      },
      children: /*#__PURE__*/_jsx(DragHandleChild, Object.assign({}, domProps, {
        $cssStyles: cssStyles,
        size: toShorthandSize(size),
        standalone: children === emptyBox,
        trailing: position === 'end',
        use: use,
        setStyledClassName: setStyledClassName,
        renderGripOnHover: renderGripOnHover,
        [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'DragHandle',
        children: children
      }))
    })
  });
};
DragHandle.displayName = 'DragHandle';